import React, { useState } from 'react'
import {
  Button,
  Col,
  Row,
  Spinner
} from 'reactstrap'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import IssueCard from './IssueCard'
import projectStringsAlertsActions from '../ProjectStringsAlerts/actions'
import ValueDisplay from './ValueDisplay'

const ConflictIssueCard = ({
  acceptingStatus,
  dispatch,
  issue,
  projectSlug,
  rejectingStatus
}) => {
  const { t } = useTranslation('ProjectsPage')
  const [isBusy, setIsBusy] = useState(false)

  const { key, locale, records } = issue
  const record = records[0] // Conflicts have a single record

  const handleAccept = () => {
    setIsBusy(true)
    dispatch(projectStringsAlertsActions.accept(projectSlug, record.id))
  }

  const handleReject = () => {
    setIsBusy(true)
    dispatch(projectStringsAlertsActions.reject(projectSlug, record.id))
  }

  // Reset busy state when operation completes
  if ((acceptingStatus === 'succeeded' || rejectingStatus === 'succeeded') && isBusy) {
    setIsBusy(false)
  }

  return (
    <IssueCard
      issueKey={key}
      issueType="conflict"
      locale={locale}
      subtitle={t('IssueCards.conflict.subtitle')}
      title={t('IssueCards.conflict.title')}
    >
      <p className="text-muted mb-3">
        {t('IssueCards.conflict.description')}
      </p>

      <code className="d-block text-muted small mb-3">{record.relative_path}</code>

      <Row>
        <Col md={6}>
          <div className="p-3 border rounded bg-light h-100">
            <div className="text-muted small mb-2 fw-semibold">
              {t('IssueCards.conflict.currentValue')}
            </div>
            <div className="conflict-value">
              <ValueDisplay value={record.value} />
            </div>
          </div>
        </Col>
        <Col md={6}>
          <div className="p-3 border rounded bg-success bg-opacity-10 border-success h-100">
            <div className="text-success small mb-2 fw-semibold">
              {t('IssueCards.conflict.newValue')}
            </div>
            <div className="conflict-value">
              <ValueDisplay value={record.new_value} />
            </div>
          </div>
        </Col>
      </Row>

      <div className="d-flex justify-content-end gap-2 mt-3">
        {isBusy ? (
          <Spinner size="sm" />
        ) : (
          <>
            <Button
              color="success"
              onClick={handleAccept}
              outline
            >
              {t('IssueCards.conflict.accept')}
            </Button>
            <Button
              color="danger"
              onClick={handleReject}
              outline
            >
              {t('IssueCards.conflict.reject')}
            </Button>
          </>
        )}
      </div>
    </IssueCard>
  )
}

ConflictIssueCard.propTypes = {
  acceptingStatus: PropTypes.string,
  dispatch: PropTypes.func.isRequired,
  issue: PropTypes.shape({
    key: PropTypes.string.isRequired,
    locale: PropTypes.string.isRequired,
    records: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      new_value: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.bool,
        PropTypes.number,
        PropTypes.string
      ]),
      relative_path: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.bool,
        PropTypes.number,
        PropTypes.string
      ])
    })).isRequired
  }).isRequired,
  projectSlug: PropTypes.string.isRequired,
  rejectingStatus: PropTypes.string
}

ConflictIssueCard.defaultProps = {
  acceptingStatus: 'idle',
  rejectingStatus: 'idle'
}

const mapStateToProps = ({
  projectStringsAlerts: { acceptingStatus, rejectingStatus }
}) => ({ acceptingStatus, rejectingStatus })

export default connect(mapStateToProps)(ConflictIssueCard)
